import {
  BillingInterval,
  CatalogItemStatus,
  CatalogItemType,
  createCatalogOfferFn,
  deleteCatalogOfferFn,
  listAllCatalogOffersOptions,
  setCatalogItemStatusFn,
  updateCatalogOfferFn,
} from "@/actions/catalog/handler";
import { listAllProgramsOptions } from "@/actions/program/handler";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import {
  Link,
  createFileRoute,
  redirect,
  useRouteContext,
} from "@tanstack/react-router";
import {
  ArrowLeft,
  CirclePlus,
  PackageOpen,
  Pencil,
  Trash2,
  X,
} from "lucide-react";
import { type FormEvent, useState } from "react";
import { toast } from "sonner";

export const Route = createFileRoute("/_app/admin/catalog")({
  beforeLoad: async ({ context }) => {
    if (!context.user || context.user.role !== "admin") {
      throw redirect({ to: "/" });
    }
  },
  loader: async ({ context }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(listAllCatalogOffersOptions()),
      context.queryClient.ensureQueryData(listAllProgramsOptions()),
    ]);
  },
  component: CatalogAdminPage,
});

const itemTypeLabels = {
  [CatalogItemType.DIGITAL_PROGRAM]: "Dijital program",
  [CatalogItemType.DIGITAL_BUNDLE]: "Dijital bundle",
  [CatalogItemType.COACHING]: "Koçluk",
  [CatalogItemType.CONSULTING]: "Danışmanlık",
  [CatalogItemType.PHYSICAL_PRODUCT]: "Fiziksel ürün",
} as const;

function slugify(text: string): string {
  return text
    .toLocaleLowerCase("tr-TR")
    .replace(/ğ/g, "g")
    .replace(/ü/g, "u")
    .replace(/ş/g, "s")
    .replace(/ı/g, "i")
    .replace(/ö/g, "o")
    .replace(/ç/g, "c")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function parseErrorMessage(error: unknown): string {
  if (!error) return "Bilinmeyen bir hata oluştu";
  const raw = (error as Error).message || String(error);
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length > 0) {
      return parsed
        .map((issue) => {
          const field = Array.isArray(issue.path)
            ? issue.path[issue.path.length - 1]
            : "";
          const fieldNames: Record<string, string> = {
            name: "Ürün adı",
            slug: "Slug",
            description: "Açıklama",
            amountKurus: "Fiyat",
            itemType: "Ürün türü",
          };
          const readableField = field ? fieldNames[field] || field : "";
          return readableField
            ? `${readableField}: ${issue.message}`
            : issue.message;
        })
        .join(" | ");
    }
  } catch {
    // not JSON
  }
  return raw;
}

function CatalogAdminPage() {
  const { queryClient } = useRouteContext({ from: "/_app/admin/catalog" });
  const { data: catalogData } = useSuspenseQuery(listAllCatalogOffersOptions());
  const { data: programsData } = useSuspenseQuery(listAllProgramsOptions());
  const offers =
    "offers" in catalogData.body ? (catalogData.body.offers ?? []) : [];
  const programs = programsData.body?.programs ?? [];

  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [isSlugTouched, setIsSlugTouched] = useState(false);
  const [description, setDescription] = useState("");
  const [itemType, setItemType] = useState<
    (typeof CatalogItemType)[keyof typeof CatalogItemType]
  >(CatalogItemType.DIGITAL_PROGRAM);
  const [amountTl, setAmountTl] = useState("");
  const [billingInterval, setBillingInterval] = useState<
    (typeof BillingInterval)[keyof typeof BillingInterval]
  >(BillingInterval.ONE_TIME);
  const [programIds, setProgramIds] = useState<string[]>([]);

  const handleCancelEdit = () => {
    setEditingItemId(null);
    setName("");
    setSlug("");
    setIsSlugTouched(false);
    setDescription("");
    setAmountTl("");
    setBillingInterval(BillingInterval.ONE_TIME);
    setItemType(CatalogItemType.DIGITAL_PROGRAM);
    setProgramIds([]);
  };

  const handleStartEdit = (offer: (typeof offers)[number]) => {
    setEditingItemId(offer.catalogItemId);
    setName(offer.name);
    setSlug(offer.slug);
    setIsSlugTouched(true);
    setDescription(offer.description || "");
    setItemType(offer.itemType);
    const price = offer.prices[0];
    if (price) {
      setAmountTl((price.amountKurus / 100).toString());
      setBillingInterval(price.billingInterval);
    } else {
      setAmountTl("");
      setBillingInterval(BillingInterval.ONE_TIME);
    }
    setProgramIds(
      offer.fulfillments.map((f) => f.referenceId).filter(Boolean) as string[],
    );
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const createMutation = useMutation({
    mutationFn: createCatalogOfferFn,
    onSuccess: async (response) => {
      if (response.status !== 201) {
        toast.error(response.body.message);
        return;
      }
      await queryClient.invalidateQueries({
        queryKey: listAllCatalogOffersOptions().queryKey,
      });
      handleCancelEdit();
      toast.success("Katalog ürünü taslak olarak oluşturuldu");
    },
    onError: (error) => toast.error(parseErrorMessage(error)),
  });

  const updateMutation = useMutation({
    mutationFn: updateCatalogOfferFn,
    onSuccess: async (response) => {
      if (response.status !== 200) {
        toast.error(response.body.message);
        return;
      }
      await queryClient.invalidateQueries({
        queryKey: listAllCatalogOffersOptions().queryKey,
      });
      handleCancelEdit();
      toast.success("Katalog ürünü güncellendi");
    },
    onError: (error) => toast.error(parseErrorMessage(error)),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteCatalogOfferFn,
    onSuccess: async (response) => {
      if (response.status !== 200) {
        toast.error(response.body.message);
        return;
      }
      await queryClient.invalidateQueries({
        queryKey: listAllCatalogOffersOptions().queryKey,
      });
      if (editingItemId) {
        handleCancelEdit();
      }
      toast.success("Katalog ürünü silindi");
    },
    onError: (error) => toast.error(parseErrorMessage(error)),
  });

  const statusMutation = useMutation({
    mutationFn: setCatalogItemStatusFn,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: listAllCatalogOffersOptions().queryKey,
      });
      toast.success("Katalog durumu güncellendi");
    },
    onError: (error) => toast.error(parseErrorMessage(error)),
  });

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!name.trim()) {
      toast.error("Ürün adı boş bırakılamaz");
      return;
    }
    const finalSlug = slug.trim() || slugify(name);
    if (!finalSlug || finalSlug.length < 3) {
      toast.error("Slug en az 3 karakter olmalıdır");
      return;
    }
    const amountKurus = Math.round(Number(amountTl.replace(",", ".")) * 100);
    if (!Number.isInteger(amountKurus) || amountKurus <= 0) {
      toast.error("Geçerli bir KDV dâhil fiyat girin");
      return;
    }

    if (editingItemId) {
      updateMutation.mutate({
        data: {
          catalogItemId: editingItemId,
          item: {
            name: name.trim(),
            slug: finalSlug,
            description: description.trim(),
            itemType,
          },
          price: {
            amountKurus,
            currency: "TRY",
            vatIncluded: true,
            vatRateBasisPoints: 2000,
            billingInterval,
            billingIntervalCount: 1,
          },
          programIds,
        },
      });
    } else {
      createMutation.mutate({
        data: {
          item: {
            name: name.trim(),
            slug: finalSlug,
            description: description.trim(),
            itemType,
            status: CatalogItemStatus.DRAFT,
          },
          price: {
            amountKurus,
            currency: "TRY",
            vatIncluded: true,
            vatRateBasisPoints: 2000,
            billingInterval,
            billingIntervalCount: 1,
          },
          programIds,
        },
      });
    }
  };

  const toggleProgram = (programId: string) => {
    setProgramIds((current) =>
      current.includes(programId)
        ? current.filter((id) => id !== programId)
        : [...current, programId],
    );
  };

  return (
    <div className="container mx-auto max-w-7xl space-y-8 px-4 py-8">
      <Button asChild variant="ghost">
        <Link to="/admin">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Admin Paneli
        </Link>
      </Button>

      <div>
        <h1 className="font-bold text-3xl">Merkezi Katalog</h1>
        <p className="mt-1 text-muted-foreground">
          KDV dâhil fiyatları, ürün türünü ve teslim edilecek programları tek
          noktadan yönetin.
        </p>
      </div>

      <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                {editingItemId ? (
                  <>
                    <Pencil className="h-5 w-5 text-primary" /> Katalog ürününü
                    düzenle
                  </>
                ) : (
                  <>
                    <CirclePlus className="h-5 w-5" /> Yeni katalog ürünü
                  </>
                )}
              </CardTitle>
              {editingItemId && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleCancelEdit}
                >
                  <X className="mr-1 h-4 w-4" /> İptal
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="catalog-name">Ürün adı</Label>
                <Input
                  id="catalog-name"
                  value={name}
                  onChange={(event) => {
                    const nextName = event.target.value;
                    setName(nextName);
                    if (!isSlugTouched) {
                      setSlug(slugify(nextName));
                    }
                  }}
                  placeholder="Örn: Evde Antrenman Programı"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="catalog-slug">Slug</Label>
                <Input
                  id="catalog-slug"
                  value={slug}
                  onChange={(event) => {
                    setIsSlugTouched(true);
                    setSlug(event.target.value);
                  }}
                  placeholder="evde-antrenman-programi"
                  pattern="[a-z0-9]+(?:-[a-z0-9]+)*"
                  required
                />
                <p className="text-[11px] text-muted-foreground">
                  Yalnızca küçük harf, rakam ve tire içerebilir (otomatik
                  oluşturulur).
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="catalog-description">Açıklama</Label>
                <Textarea
                  id="catalog-description"
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  placeholder="Program veya ürünün içeriği hakkında bilgi..."
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="catalog-type">Ürün türü</Label>
                <select
                  id="catalog-type"
                  value={itemType}
                  onChange={(event) =>
                    setItemType(
                      event.target
                        .value as (typeof CatalogItemType)[keyof typeof CatalogItemType],
                    )
                  }
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  {Object.entries(itemTypeLabels).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="catalog-price">KDV dâhil fiyat (TL)</Label>
                  <Input
                    id="catalog-price"
                    inputMode="decimal"
                    value={amountTl}
                    onChange={(event) => setAmountTl(event.target.value)}
                    placeholder="990"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="catalog-interval">Tahsilat</Label>
                  <select
                    id="catalog-interval"
                    value={billingInterval}
                    onChange={(event) =>
                      setBillingInterval(
                        event.target
                          .value as (typeof BillingInterval)[keyof typeof BillingInterval],
                      )
                    }
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value={BillingInterval.ONE_TIME}>Tek sefer</option>
                    <option value={BillingInterval.MONTH}>Aylık</option>
                  </select>
                </div>
              </div>
              {programs.length > 0 && (
                <fieldset className="space-y-2">
                  <legend className="font-medium text-sm">
                    Açılacak programlar
                  </legend>
                  <div className="max-h-44 space-y-2 overflow-y-auto rounded-md border p-3">
                    {programs.map((program) => (
                      <label
                        key={program.programId}
                        className="flex items-center gap-2 text-sm"
                      >
                        <input
                          type="checkbox"
                          checked={programIds.includes(program.programId)}
                          onChange={() => toggleProgram(program.programId)}
                        />
                        {program.name}
                      </label>
                    ))}
                  </div>
                </fieldset>
              )}
              <div className="flex gap-2">
                {editingItemId && (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-1/3"
                    onClick={handleCancelEdit}
                  >
                    Vazgeç
                  </Button>
                )}
                <Button
                  type="submit"
                  className={editingItemId ? "w-2/3" : "w-full"}
                  disabled={
                    createMutation.isPending || updateMutation.isPending
                  }
                >
                  {editingItemId
                    ? updateMutation.isPending
                      ? "Kaydediliyor..."
                      : "Değişiklikleri Kaydet"
                    : createMutation.isPending
                      ? "Oluşturuluyor..."
                      : "Taslak Oluştur"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <div className="space-y-4">
          {offers.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center py-12 text-center">
                <PackageOpen className="mb-3 h-10 w-10 text-muted-foreground" />
                <p className="font-medium">Henüz katalog ürünü yok</p>
              </CardContent>
            </Card>
          ) : (
            offers.map((offer) => {
              const price = offer.prices[0];
              const linkedPrograms = offer.fulfillments
                .map((fulfillment) =>
                  programs.find(
                    (program) => program.programId === fulfillment.referenceId,
                  ),
                )
                .filter(Boolean);
              return (
                <Card key={offer.catalogItemId}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <CardTitle>{offer.name}</CardTitle>
                        <p className="mt-1 text-muted-foreground text-sm">
                          {itemTypeLabels[offer.itemType]}
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Badge variant="outline">{offer.status}</Badge>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground hover:text-destructive"
                          disabled={deleteMutation.isPending}
                          title="Ürünü Sil"
                          onClick={() => {
                            if (
                              window.confirm(
                                `"${offer.name}" ürününü silmek istediğinize emin misiniz?`,
                              )
                            ) {
                              deleteMutation.mutate({
                                data: { catalogItemId: offer.catalogItemId },
                              });
                            }
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {offer.description && (
                      <p className="whitespace-pre-line text-muted-foreground text-sm">
                        {offer.description}
                      </p>
                    )}
                    <div className="font-bold text-2xl">
                      {price
                        ? `${(price.amountKurus / 100).toLocaleString("tr-TR")} TL${price.billingInterval === BillingInterval.MONTH ? "/ay" : ""}`
                        : "Fiyat yok"}
                    </div>
                    {linkedPrograms.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {linkedPrograms.map((program) =>
                          program ? (
                            <Badge key={program.programId} variant="secondary">
                              {program.name}
                            </Badge>
                          ) : null,
                        )}
                      </div>
                    )}
                    <div className="flex items-center gap-2 pt-1">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleStartEdit(offer)}
                      >
                        <Pencil className="mr-1.5 h-3.5 w-3.5" /> Düzenle
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={statusMutation.isPending}
                        onClick={() =>
                          statusMutation.mutate({
                            data: {
                              catalogItemId: offer.catalogItemId,
                              status:
                                offer.status === CatalogItemStatus.ACTIVE
                                  ? CatalogItemStatus.DRAFT
                                  : CatalogItemStatus.ACTIVE,
                            },
                          })
                        }
                      >
                        {offer.status === CatalogItemStatus.ACTIVE
                          ? "Satıştan Kaldır"
                          : "Satışa Aç"}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
